import React, { useState, useEffect } from 'react';
import { contactApi } from '../../services/api/contact';
import './Contact.css';

export const Contact: React.FC = () => {
  useEffect(() => { document.title = 'Submit Your Game | Practical Media'; }, []);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await contactApi.submitContact(formData);
      setStatus('sent');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const faqs = [
    { q: "What size teams do you typically work with?", a: "We focus on small to medium-sized indie games and solo developers. We handle marketing, localization, Steam page optimization, and all the admin tasks that pull you away from development." },
    { q: "How does the performance-based model work?", a: "We actively promote your game through targeted campaigns, social media, and content creation. Instead of charging upfront, we take a percentage of sales we directly generate. Every sale is tracked transparently." },
    { q: "Can I work with other publishers too?", a: "Absolutely. Maybe you need a publisher for console porting or specific regions. We're happy to work alongside other partners. Our only requirement is clear attribution for the sales we generate." },
    { q: "What if I need something you don't offer?", a: "We'll help you find the right partners. Console porting, localization, technical support - we've built relationships with trusted specialists and can connect you." },
  ];

  const deal = [
    { h: '100% IP ownership', p: 'Your game, your code, your brand. You keep everything.' },
    { h: 'Performance-based split', p: 'No upfront fees. We earn a percentage of sales we generate.' },
    { h: 'Real-time metrics', p: 'Every sale is tracked transparently, so you always see the numbers.' },
    { h: 'Post-launch support', p: 'We keep promoting and helping after launch day.' },
  ];

  const check = (
    <svg className="ct-chk" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
  );

  return (
    <div className="contact-page">
      <div className="container">
        <header className="ct-hero">
          <p className="ct-eyebrow">Submit</p>
          <h1 className="ct-title">Launch your game with us</h1>
          <p className="ct-lead">Submit your game and let's build something together.</p>
          <div className="ct-cta-row">
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfhx_Y_o3rB9Liw2iPJ2lhlmdgup7EyFkNnmOboTHmOiBUmIg/viewform"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="primary-btn"
            >
              Game Submission Form
            </a>
            <a href="https://discord.com/invite/U7dYpq5yMK" target="_blank" rel="noopener noreferrer nofollow" className="btn-ghost">
              Chat on Discord
            </a>
          </div>
        </header>

        <section className="ct-section">
          <p className="ct-sec-label">The deal</p>
          <div className="ct-deal">
            {deal.map(d => (
              <div key={d.h} className="ct-deal-item">
                {check}
                <div>
                  <h2>{d.h}</h2>
                  <p>{d.p}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="ct-section">
          <div className="ct-panel">
            <p className="ct-sec-label">Send a message</p>
            <p className="ct-panel-sub">Have a question or want to discuss your game?</p>

            {status === 'sent' ? (
              <p className="ct-sent">Sent. We'll get back to you soon.</p>
            ) : (
              <form onSubmit={handleSubmit} className="form">
                <div className="form-row">
                  <div className="field">
                    <label className="label" htmlFor="name">Name</label>
                    <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
                  </div>
                </div>
                <div className="field">
                  <label className="label" htmlFor="subject">Subject</label>
                  <input id="subject" name="subject" type="text" value={formData.subject} onChange={handleChange} required />
                </div>
                <div className="field">
                  <label className="label" htmlFor="message">Message</label>
                  <textarea id="message" name="message" rows={6} value={formData.message} onChange={handleChange} required placeholder="What's your game about? What kind of help are you looking for?" />
                </div>
                <button type="submit" className="primary-btn" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                {status === 'error' && <p className="error-text">Something went wrong. Try again.</p>}
              </form>
            )}

            <p className="ct-other">
              Other ways to connect: <a href="https://discord.com/invite/U7dYpq5yMK" target="_blank" rel="noopener noreferrer nofollow">Discord</a> for community chat and quick responses, or <a href="mailto:contact@practicalmedia.com">contact@practicalmedia.com</a> for formal inquiries.
            </p>
          </div>
        </section>

        <section className="ct-section">
          <p className="ct-sec-label">FAQ</p>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <span className="faq-arrow">{openFaq === i ? '-' : '+'}</span>
                </button>
                {openFaq === i && <p className="faq-a">{faq.a}</p>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
